import {
  PnoeDeviceGeneration,
  PnoeDeviceRef,
  PnoeMeasurementCatalogs,
} from "../entities/pnoeMeasurement";

/**
 * How a PNOĒ breath test names the hardware that recorded it.
 *
 * Since the 13 Oct 2026 contract the service sends no device *name* — only a
 * serial number and the hardware generation (`pnoe1`/`pnoe2`, null on records
 * older than generation tracking). The listing's Device column and the detail
 * page's "PNOĒ device" field both read "PNOĒ 1 · PNOE 2316-135"; this is the
 * one place that format lives, so changing it changes both.
 *
 * Every function returns `null` rather than "—" for nothing-to-show: the two
 * consumers each have their own empty treatment (`PnoeField` renders the em
 * dash; a History row's `device: null` reads like a lab row's), and a "—"
 * returned here would otherwise end up as "— · serial".
 */

const DEVICE_GENERATION_FALLBACK: Record<PnoeDeviceGeneration, string> = {
  pnoe1: "PNOĒ 1",
  pnoe2: "PNOĒ 2",
};

export const DEVICE_LABEL_SEPARATOR = " · ";

/** Catalog label → hardcoded English → the raw value; null for no generation. */
export const getDeviceGenerationLabel = (
  generation: PnoeDeviceGeneration | null | undefined,
  catalogs?: PnoeMeasurementCatalogs | null
): string | null => {
  if (!generation) return null;
  // `device_pnoe_generations` is optional: a configuration payload cached
  // before the contract change has no such catalog.
  const fromCatalog = catalogs?.device_pnoe_generations?.find(
    (option) => option.value === generation
  )?.label;
  return fromCatalog || DEVICE_GENERATION_FALLBACK[generation] || generation;
};

/** Trims, drops the empties, joins what is left; null when nothing is. */
export const joinDeviceLabel = (
  parts: (string | null | undefined)[]
): string | null => {
  const kept = parts
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);
  return kept.length ? kept.join(DEVICE_LABEL_SEPARATOR) : null;
};

/** "PNOĒ 1 · PNOE 2316-135"; the serial alone on a record with no generation. */
export const formatPnoeDevice = (
  device: Partial<PnoeDeviceRef> | null | undefined,
  catalogs?: PnoeMeasurementCatalogs | null
): string | null =>
  joinDeviceLabel([
    getDeviceGenerationLabel(device?.device_pnoe_generation, catalogs),
    device?.device_pnoe_serial_number,
  ]);

/** "Polar · 9C73702A"; the serial alone when the service sent no brand. */
export const formatHrDevice = (
  device:
    | {
        device_hr_brand?: string | null;
        device_hr_serial_number?: string | null;
      }
    | null
    | undefined
): string | null =>
  joinDeviceLabel([device?.device_hr_brand, device?.device_hr_serial_number]);
